import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useAppearance } from "@/lib/appearance";
import PwaControls from "@/components/PwaControls";
import {
  House, Package, ArrowsLeftRight, ChartBar, Tag, Truck,
  SignOut, List, Gear, X, CaretRight
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", description: "Riepilogo generale", icon: House, end: true, testid: "nav-dashboard" },
  { to: "/prodotti", label: "Lista merci", description: "Articoli e giacenze", icon: Package, testid: "nav-products" },
  { to: "/movimenti", label: "Carico / Scarico", description: "Movimenti di magazzino", icon: ArrowsLeftRight, testid: "nav-movements" },
  { to: "/categorie", label: "Categorie", description: "Classificazione articoli", icon: Tag, testid: "nav-categories" },
  { to: "/fornitori", label: "Fornitori", description: "Anagrafica fornitori", icon: Truck, testid: "nav-suppliers" },
  { to: "/report", label: "Report", description: "Analisi e statistiche", icon: ChartBar, testid: "nav-reports" },
  { to: "/impostazioni", label: "Impostazioni", description: "Configurazione gestionale", icon: Gear, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { appearance } = useAppearance();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const current =
    NAV.find((item) =>
      item.end
        ? location.pathname === "/"
        : location.pathname.startsWith(item.to) && item.to !== "/"
    ) ?? NAV[0];

  const darkSidebar = appearance.sidebarStyle === "dark";
  const sidebarClasses = darkSidebar
    ? "bg-slate-950 border-slate-800 text-white"
    : "bg-white border-slate-200 text-slate-900";

  const getUserInitials = () => {
    const source = user?.name || user?.email || "A";
    return source
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  };

  return (
    <div className="min-h-screen bg-slate-100 flex app-shell">
      {open && (
        <button
          type="button"
          aria-label="Chiudi menu"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-sm lg:hidden"
        />
      )}

      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-40 flex w-72 flex-col border-r transition-transform duration-300 ease-out lg:static lg:translate-x-0 ${sidebarClasses} ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className={`flex min-h-20 items-center gap-3 border-b px-5 ${darkSidebar ? "border-slate-800" : "border-slate-200"}`}>
          <div
            className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-white shadow-lg"
            style={{
              backgroundColor: appearance.primaryColor,
              boxShadow: `0 10px 25px ${appearance.primaryColor}30`,
            }}
          >
            <Package weight="fill" size={23} />
          </div>

          <div className="min-w-0 flex-1">
            <div className={`truncate text-base font-bold tracking-tight ${darkSidebar ? "text-white" : "text-slate-950"}`}>
              {appearance.appName}
            </div>
            {appearance.showSubtitle && (
              <div className={`truncate text-xs ${darkSidebar ? "text-slate-400" : "text-slate-500"}`}>
                {appearance.subtitle}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Chiudi menu"
            className={`rounded-xl p-2 lg:hidden ${darkSidebar ? "text-slate-400 hover:bg-slate-900 hover:text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"}`}
          >
            <X size={21} weight="bold" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5">
          <div className={`mb-3 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] ${darkSidebar ? "text-slate-500" : "text-slate-400"}`}>
            Navigazione
          </div>

          <div className="space-y-1.5">
            {NAV.map(({ to, label, description, icon: Icon, end, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-xl px-3 py-3 transition ${
                    isActive
                      ? "text-white shadow-md"
                      : darkSidebar
                        ? "text-slate-300 hover:bg-slate-900 hover:text-white"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                  }`
                }
                style={({ isActive }) =>
                  isActive
                    ? {
                        backgroundColor: appearance.primaryColor,
                        boxShadow: `0 8px 20px ${appearance.primaryColor}25`,
                      }
                    : undefined
                }
              >
                {({ isActive }) => (
                  <>
                    <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${
                      isActive
                        ? "bg-white/15"
                        : darkSidebar
                          ? "bg-slate-900 group-hover:bg-slate-800"
                          : "bg-slate-100 group-hover:bg-white"
                    }`}>
                      <Icon weight={isActive ? "fill" : "bold"} size={19} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">{label}</div>
                      <div className={`truncate text-[11px] ${
                        isActive
                          ? "text-white/70"
                          : darkSidebar
                            ? "text-slate-500"
                            : "text-slate-400"
                      }`}>
                        {description}
                      </div>
                    </div>

                    <CaretRight
                      size={15}
                      weight="bold"
                      className={isActive ? "text-white/80" : "opacity-0 transition group-hover:opacity-50"}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className={`border-t p-3 ${darkSidebar ? "border-slate-800" : "border-slate-200"}`}>
          <div className={`mb-2 flex items-center gap-3 rounded-xl p-3 ${darkSidebar ? "bg-slate-900" : "bg-slate-100"}`}>
            <div
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-bold text-white"
              style={{ backgroundColor: appearance.primaryColor }}
            >
              {getUserInitials()}
            </div>

            <div className="min-w-0 flex-1">
              <div className={`truncate text-sm font-semibold ${darkSidebar ? "text-white" : "text-slate-900"}`}>
                {user?.name || "Amministratore"}
              </div>
              <div className={`truncate text-xs ${darkSidebar ? "text-slate-400" : "text-slate-500"}`} data-testid="current-user-email">
                {user?.email}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={logout}
            data-testid="logout-btn"
            className={`flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
              darkSidebar
                ? "text-slate-300 hover:bg-red-950/40 hover:text-red-300"
                : "text-slate-600 hover:bg-red-50 hover:text-red-600"
            }`}
          >
            <SignOut weight="bold" size={18} />
            Esci dal gestionale
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex min-h-20 items-center gap-4 border-b border-slate-200 bg-white/95 px-4 backdrop-blur lg:px-8">
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Apri menu"
            className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-700 shadow-sm hover:bg-slate-50 lg:hidden"
          >
            <List weight="bold" size={22} />
          </button>

          <div className="min-w-0 flex-1">
            <div className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
              Gestionale magazzino
            </div>
            <div className="truncate text-xl font-bold tracking-tight text-slate-950" data-testid="page-title">
              {current.label}
            </div>
          </div>

          <PwaControls />

          <div className="hidden items-center gap-3 md:flex">
            <div className="text-right">
              <div className="text-sm font-semibold text-slate-900">
                {user?.name || "Amministratore"}
              </div>
              <div className="text-xs text-slate-500">
                {user?.role === "admin" ? "Accesso amministratore" : "Utente gestionale"}
              </div>
            </div>

            <div
              className="grid h-10 w-10 place-items-center rounded-full text-sm font-bold text-white"
              style={{ backgroundColor: appearance.primaryColor }}
            >
              {getUserInitials()}
            </div>
          </div>
        </header>

        <OfflineBanner />

        <main className="flex-1 p-4 sm:p-6 lg:p-8" data-testid="main-content">
          <div className="mx-auto w-full max-w-[1600px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function OfflineBanner() {
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (online) return null;

  return (
    <div className="border-b border-orange-200 bg-orange-50 px-4 py-2 text-center text-xs font-medium text-orange-800">
      Modalità offline: puoi consultare i dati già caricati, ma carichi, scarichi e modifiche non sono disponibili.
    </div>
  );
}
